import { FC, useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { useFormContext } from 'react-hook-form';
import { DecimalService, LocalizedCurrencyValue, LocalizedText } from '@mews/framework';
import { OrderItem } from '@mews-commander/data-access';
import { Body, Stack, Title } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { EditItemDialogFields, EditItemFormValues } from './constants';
import { useCalculateEditAmount } from './hooks/useCalculateEditAmount';

interface EditItemDialogTotalAmountProps {
    orderItem: OrderItem;
}

export const EditItemDialogTotalAmount: FC<EditItemDialogTotalAmountProps> = ({ orderItem }) => {
    const { watch } = useFormContext<EditItemFormValues>();

    const quantity = watch(EditItemDialogFields.Quantity);
    const unitPrice = watch(EditItemDialogFields.UnitPrice);

    const totalAmount = useMemo(() => {
        const { multiply, parse, toNumber } = DecimalService;

        if (!quantity || !unitPrice || Number.isNaN(unitPrice)) {
            return null;
        }

        const valueBeforeMultiplers = multiply(parse(quantity), parse(unitPrice));
        const value = pipe(
            multiply(valueBeforeMultiplers, parse(orderItem.personMultiplier)),
            (val) => multiply(val, parse(orderItem.nightMultiplier)),
            toNumber
        );

        try {
            return {
                value,
                currency: orderItem.unitAmount.currency,
            };
        } catch (err) {
            return {
                value: 0,
                currency: orderItem?.unitAmount?.currency,
            };
        }
    }, [quantity, unitPrice, orderItem]);

    const { value, totalTaxValue } = useCalculateEditAmount({
        price: totalAmount?.value || 0,
        taxValues: orderItem.totalAmount.taxValues || [],
    });

    if (!totalAmount) {
        return null;
    }

    return (
        <Stack vertical itemAlignment="end">
            <Body size="small" variant="secondary">
                <LocalizedText textKey={TextKey.Tax} />
                {UnicodeEntity.Colon}
                {UnicodeEntity.NoBreakSpace}
                <LocalizedCurrencyValue value={{ value: totalTaxValue, currency: totalAmount.currency }} />
            </Body>
            <Title level={2}>
                <LocalizedText textKey={TextKey.TotalInclTax} />
                {UnicodeEntity.Colon}
                {UnicodeEntity.NoBreakSpace}
                <LocalizedCurrencyValue value={{ value, currency: totalAmount.currency }} />
            </Title>
        </Stack>
    );
};
